import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SignOut, Gear, User, CaretDown, ShieldCheck, Key } from '@phosphor-icons/react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import ChangePasswordDialog from './ChangePasswordDialog';
import AgMark from './brand/AgMark';
import TI from './brand/TI';

const AG_TAGLINE = "Independent toxicological evaluation. Exposure-based risk.";

/** ToxIntel primary word-mark — rendered via the canonical <TI /> component
 * to enforce single-word kerning, italic "Intel", and consistent coloring
 * across header, login, footer, and loading/auth states. Rendered as type
 * (no PNG) so it stays crisp at every zoom and size. */
export function ToxIntelMark({ size = 'default', showPoweredBy = false, className = '' }) {
  const baseSize = size === 'small' ? 'text-xl' : size === 'large' ? 'text-4xl' : 'text-2xl';
  return (
    <span className={`inline-flex flex-col items-start leading-none ${className}`} aria-label="ToxIntel">
      <span className={`${baseSize} font-extrabold tracking-tight text-[#0B1426]`}>
        <TI color="#E63946" />
      </span>
      {showPoweredBy && (
        <span className="mt-1 text-[9px] font-medium tracking-[0.18em] uppercase text-[#5B6577]">
          by A<sup className="text-[7px]">G</sup>
        </span>
      )}
    </span>
  );
}

// Backwards-compat aliases so existing imports don't break during the rebrand.
export const AgLogo = ToxIntelMark;
export function AgTagline({ className = '' }) {
  return <span className={`text-[#4B5563] ${className}`}>{AG_TAGLINE}</span>;
}

export function SiteHeader() {
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const [changePwOpen, setChangePwOpen] = useState(false);
  const handleLogout = async () => { await logout(); };

  return (
    <header className="bg-white sticky top-0 z-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        {/* Brand lockup — Aᴳ parent logo links to tagc.us (same tab);
            ToxIntel wordmark links to the product home. */}
        <div className="flex items-center gap-3" data-testid="site-logo">
          <a
            href="https://tagc.us"
            className="flex items-center"
            data-testid="site-logo-ag"
            aria-label="Aᴳ — The Agyekum Group"
          >
            <AgMark size={36} variant="light" />
          </a>
          <Link to="/" data-testid="site-logo-toxintel" aria-label="ToxIntel home">
            <ToxIntelMark size="default" showPoweredBy />
          </Link>
        </div>

        {/* Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="text-sm font-medium text-[#4B5563] hover:text-[#111827] transition-colors" data-testid="nav-dashboard">Portal</Link>
              <Link to="/copilot" className="text-sm font-medium text-[#FF4E6A] hover:text-[#D63A56] transition-colors flex items-center gap-1" data-testid="nav-copilot">
                Copilot<sup className="text-[8px]">™</sup>
              </Link>
              <Link to="/passport" className="text-sm font-medium text-[#4B5563] hover:text-[#0B2447] transition-colors flex items-center gap-1" data-testid="nav-passport">
                Passport<sup className="text-[8px]">™</sup>
              </Link>
              <Link to="/upload" className="text-sm font-medium text-[#4B5563] hover:text-[#111827] transition-colors" data-testid="nav-new-assessment">New Assessment</Link>
              <Link to="/library" className="text-sm font-medium text-[#4B5563] hover:text-[#111827] transition-colors" data-testid="nav-library">Library</Link>
              <Link to="/marketplace" className="text-sm font-medium text-[#4B5563] hover:text-[#111827] transition-colors" data-testid="nav-marketplace">Marketplace</Link>
              {isAdmin && (
                <Link to="/assessor" className="text-sm font-medium text-[#FF4E6A] hover:text-[#D63A56] transition-colors" data-testid="nav-assessor">Assessor</Link>
              )}
              {isAdmin && (
                <Link to="/admin" className="text-sm font-medium text-[#4B5563] hover:text-[#111827] transition-colors" data-testid="nav-admin">
                  <Gear size={15} className="inline mr-1" />Admin
                </Link>
              )}
            </>
          ) : (
            <>
              <a href="#services" className="text-sm font-medium text-[#4B5563] hover:text-[#111827] transition-colors">Services</a>
              <a href="#how-it-works" className="text-sm font-medium text-[#4B5563] hover:text-[#111827] transition-colors">Process</a>
              <Link to="/marketplace" className="text-sm font-medium text-[#FF4E6A] hover:text-[#D63A56] transition-colors" data-testid="nav-marketplace-public">Marketplace</Link>
            </>
          )}
        </nav>

        {/* User / Auth */}
        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-50 transition-colors" data-testid="user-menu-trigger">
                  <div className="w-7 h-7 bg-[#111827] rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">{user?.name?.charAt(0).toUpperCase() || 'U'}</span>
                  </div>
                  <span className="text-sm font-medium text-[#111827] hidden sm:block">{user?.name}</span>
                  <CaretDown size={12} className="text-[#4B5563]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-[#111827]">{user?.name}</p>
                  <p className="text-xs text-[#4B5563]">{user?.email}</p>
                  {user?.company && <p className="text-xs text-[#4B5563]">{user?.company}</p>}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="cursor-pointer"><Link to="/dashboard"><User size={16} className="mr-2" /> My Portal</Link></DropdownMenuItem>
                {isAdmin && <DropdownMenuItem asChild className="cursor-pointer"><Link to="/admin"><Gear size={16} className="mr-2" /> Admin Dashboard</Link></DropdownMenuItem>}
                {isAdmin && <DropdownMenuItem asChild className="cursor-pointer"><Link to="/assessor"><ShieldCheck size={16} className="mr-2" /> Assessor Console</Link></DropdownMenuItem>}
                <DropdownMenuItem className="cursor-pointer" onClick={() => setChangePwOpen(true)} data-testid="menu-change-password">
                  <Key size={16} className="mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer text-red-600" onClick={handleLogout} data-testid="logout-btn"><SignOut size={16} className="mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm font-semibold text-[#111827] border border-[#111827] hover:bg-[#111827] hover:text-white px-4 py-2 rounded transition-colors"
                data-testid="nav-login"
              >
                Sign In
              </Link>
              <Link to="/register" className="text-sm font-semibold text-white bg-[#FF4E6A] hover:bg-[#E04057] px-4 py-2 rounded transition-colors" data-testid="nav-register">Get Started</Link>
            </>
          )}
        </div>
      </div>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="bg-[#111827] text-white mt-auto">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="mb-4 flex items-center gap-3">
              <AgMark size={42} variant="dark" />
              <span className="text-2xl font-extrabold tracking-tight text-white">
                <TI color="#FF4E6A" />
              </span>
              <span className="ml-1 text-[10px] font-medium tracking-[0.1em] text-white/50">
                by Aᴳ
              </span>
            </div>
            <p className="text-sm text-white/50 max-w-md leading-relaxed mb-3">{AG_TAGLINE}</p>
            <p className="text-sm text-white/40 leading-relaxed">
              Scientific governance for beauty, personal care, and ingestible brands.
            </p>
          </div>
          {/* Services */}
          <div>
            <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40 mb-3">Services</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link to="/upload" className="hover:text-white transition-colors"><TI color="#FF4E6A" /> Risk Screen</Link></li>
              <li><Link to="/upload" className="hover:text-white transition-colors">Safety Assessment (CPSR)</Link></li>
              <li><Link to="/register" className="hover:text-white transition-colors">Client Portal</Link></li>
            </ul>
          </div>
          {/* Contact */}
          <div>
            <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40 mb-3">Contact</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li>regulatory@tagc.us</li>
              <li><a href="https://tagc.us" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">tagc.us</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/30">&copy; {new Date().getFullYear()} <TI color="#FF4E6A" />&nbsp;by A<sup>G</sup>. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
